import React, { useState } from "react";
import {
  AiFillCheckCircle,
  AiFillWarning,
  AiOutlineCalendar,
} from "react-icons/ai";
import {
  FaUser,
  FaHeartbeat,
  FaProcedures,
  FaMoneyBill,
  FaFileMedical,
} from "react-icons/fa";
import { MdNotes, MdPersonOutline } from "react-icons/md";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

export default function PatientDashboard({
  patient,
  vitals,
  treatments,
  billing,
}) {
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = [
    { id: "overview", label: "Overview", icon: <FaUser /> },
    { id: "vitals", label: "Vitals", icon: <FaHeartbeat /> },
    { id: "treatment", label: "Treatment Plan", icon: <FaProcedures /> },
    { id: "billing", label: "Billing Summary", icon: <FaMoneyBill /> },
    { id: "medical", label: "Medical Records", icon: <FaFileMedical /> },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  const renderOverview = () => (
    <div>
      <h2 className="text-xl font-bold">Patient Overview</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <p>
            <strong>Name:</strong> {patient.name}
          </p>
          <p>
            <strong>Age:</strong> {patient.age}
          </p>
          <p>
            <strong>Gender:</strong> {patient.gender}
          </p>
          <p>
            <strong>Contact:</strong> {patient.contact}
          </p>
          <p>
            <strong>Address:</strong> {patient.address}
          </p>
        </div>
        <div>
          <p>
            <strong>Admission Date:</strong> {patient.admissionDate}
          </p>
          <p>
            <strong>Doctor:</strong> {patient.doctor}
          </p>
          <p>
            <strong>Ward:</strong> {patient.ward}
          </p>
          <p>
            <strong>Bed:</strong> {patient.bed || "Not Assigned"}
          </p>
          <p>
            <strong>Emergency Contact:</strong> {patient.emergencyContact}
          </p>
        </div>
      </div>
    </div>
  );

  const renderVitals = () => {
    if (vitals.length === 0) {
      return <p className="text-gray-500 mt-4">No vitals recorded yet.</p>;
    }

    const vitalsData = vitals.map((vital) => ({
      name: `${vital.date} ${vital.time}`,
      temperature: vital.temperature,
      systolicBP: parseInt(vital.bloodPressure.split("/")[0]),
      heartRate: vital.heartRate,
    }));

    return (
      <div>
        <h2 className="text-xl font-bold">Vitals</h2>
        {/* Chart Section */}
        <div className="mt-4" style={{ height: "300px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={vitalsData}
              margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="temperature"
                stroke="#FF6384"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="systolicBP"
                stroke="#36A2EB"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="heartRate"
                stroke="#4BC0C0"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Table Section */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold">Vitals Table</h3>
          <table className="min-w-full mt-4 border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-gray-300 px-4 py-2">
                  Date & Time
                </th>
                <th className="border border-gray-300 px-4 py-2">
                  Temperature (°C)
                </th>
                <th className="border border-gray-300 px-4 py-2">
                  Systolic BP (mmHg)
                </th>
                <th className="border border-gray-300 px-4 py-2">
                  Heart Rate (bpm)
                </th>
              </tr>
            </thead>
            <tbody>
              {vitalsData.map((vital, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-100"}
                >
                  <td className="border border-gray-300 px-4 py-2">
                    {vital.name}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {vital.temperature}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {vital.systolicBP}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {vital.heartRate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTreatmentPlan = () => {
    if (treatments.length === 0) {
      return (
        <div className="text-center mt-4">
          <p className="text-gray-500 text-lg flex items-center justify-center">
            <AiFillWarning className="w-6 h-6 text-gray-400 mr-2" />
            No treatment plan available.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800">Treatment Plan</h2>
        <ul className="mt-6 space-y-6">
          {treatments.map((treatment, index) => (
            <li
              key={index}
              className="p-4 border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-center mb-3">
                <MdNotes className="w-6 h-6 text-blue-500 mr-3" />
                <p className="text-lg font-semibold text-gray-800">
                  {treatment.name}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <p className="flex items-center">
                  <AiFillCheckCircle
                    className={`w-5 h-5 mr-2 ${
                      treatment.status === "Completed"
                        ? "text-green-500"
                        : "text-yellow-500"
                    }`}
                  />
                  <strong>Status:</strong> {treatment.status}
                </p>
                <p className="flex items-center">
                  <AiOutlineCalendar className="w-5 h-5 text-gray-500 mr-2" />
                  <strong>Date:</strong> {treatment.date}
                </p>
                <p className="flex items-center">
                  <MdPersonOutline className="w-5 h-5 text-gray-500 mr-2" />
                  <strong>Doctor:</strong> {treatment.doctor}
                </p>
                <p className="col-span-full">
                  <strong>Notes:</strong>{" "}
                  {treatment.notes || "No additional notes provided."}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };
  const renderBillingSummary = () => {
    const paidItems = billing.items.filter((item) => item.paid).length;
    const unpaidItems = billing.items.length - paidItems;

    const billingGraphData = [
      { name: "Paid", value: paidItems },
      { name: "Due", value: unpaidItems },
    ];

    const COLORS = ["#4CAF50", "#FF5252"]; // Green for Paid, Red for Due

    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-800">Billing Summary</h2>
        <div className="mt-6 flex flex-col md:flex-row items-center gap-8">
          {/* Pie Chart Section */}
          <div className="w-full md:w-1/2 h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={billingGraphData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  label
                >
                  {billingGraphData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Billing Table Section */}
          <div className="w-full md:w-1/2">
            <table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-100 text-gray-800 text-sm font-semibold">
                  <th className="border px-4 py-2 text-left">Description</th>
                  <th className="border px-4 py-2 text-right">Amount (₹)</th>
                  <th className="border px-4 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {billing.items.map((item, index) => (
                  <tr
                    key={index}
                    className={`text-gray-700 text-sm ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-gray-100`}
                  >
                    <td className="border px-4 py-2">{item.description}</td>
                    <td className="border px-4 py-2 text-right">
                      {item.amount}
                    </td>
                    <td className="border px-4 py-2 text-center">
                      {item.paid ? (
                        <span className="text-green-500 font-semibold">
                          Paid
                        </span>
                      ) : (
                        <span className="text-red-500 font-semibold">Due</span>
                      )}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gray-100 font-bold text-gray-800">
                  <td className="border px-4 py-2">Total</td>
                  <td className="border px-4 py-2 text-right">
                    ₹{billing.total}
                  </td>
                  <td className="border px-4 py-2 text-center">
                    {billing.due > 0 ? (
                      <span className="text-red-500">₹{billing.due} Due</span>
                    ) : (
                      <span className="text-green-500">All Paid</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderMedicalRecords = () => {
    return (
      <div>
        <h2 className="text-xl font-bold">Medical Records</h2>
        <p className="text-gray-500 mt-4">
          Medical records functionality coming soon...
        </p>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return renderOverview();
      case "vitals":
        return renderVitals();
      case "treatment":
        return renderTreatmentPlan();
      case "billing":
        return renderBillingSummary();
      case "medical":
        return renderMedicalRecords();
      default:
        return null;
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-700">Patient Dashboard</h1>
        <p className="text-gray-500 mt-2">
          Comprehensive view for {patient.name}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              activeTab === tab.id
                ? "bg-blue-500 text-white"
                : "bg-gray-200 text-gray-600 hover:bg-gray-300"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="inline-flex items-center space-x-2">
              {tab.icon}
              <span>{tab.label}</span>
            </span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white p-6 rounded-lg shadow">{renderContent()}</div>
    </div>
  );
}
